import React, { FC, useState, useEffect, useContext, useRef } from 'react';
import useIsInViewport from '~/hooks/useInViewport';
import { PAGE_ROUTES } from '~/constants/pages';
import { FuelContext } from '~/providers/FuelProvider';
import GDTBannerStyled from './GDTBannerStyled';
import Button from '~/components/Button';
import { trackEvent } from '~/helpers/tracking';
import { useDispatch } from 'react-redux';
import { setQuiz } from '~/redux/actions/quiz';
import useWindowSize from '~/hooks/useWindowSize';
import { CloseThin } from '../Icons/fill';

interface Props {
  enabled: string;
  title?: string;
  subtitle?: string;
  backgroundColor?: string;
  button?: {
    text: string;
    backgroundColor: string;
    textColor: string;
  };
  delay?: {
    enabled: string;
    time: string;
  };
  sticky?: string;
}

const GDTBanner: FC<Props> = ({ enabled, title, subtitle, backgroundColor, button, delay, sticky }) => {
  const dispatch = useDispatch();
  const { router } = useContext(FuelContext);
  const [show, setShow] = useState(false);
  const gdtBannerRef = useRef();
  const { mobile } = useWindowSize();

  const inViewport = useIsInViewport(gdtBannerRef);
  useEffect(() => {
    if (inViewport) {
      trackEvent({
        action: 'elementViewed',
        data: {
          elementType: 'BANNER',
          location: 'GDT cart link off',
          position: 'GDT CTA',
          text: 'take the quiz',
        },
      });
    }
  }, [inViewport]);

  useEffect(() => {
    let timeoutId;
    if (enabled === 'true') {
      if (delay && delay.enabled === 'true') {
        timeoutId = setTimeout(() => {
          setShow(true);
        }, Number(delay.time) * 1000);
      } else {
        setShow(true);
      }
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [enabled, delay]);

  const eventHandler = (event) => {
    event.preventDefault();
    trackEvent({
      action: 'elementClicked',
      data: {
        elementType: 'BUTTON',
        location: 'GDT cart link off',
        position: 'GDT CTA',
        text: 'take the quiz',
      },
    });
    dispatch(setQuiz('quizStarted', true));
    router.push(`${PAGE_ROUTES.MY_CUSTOM_QUOTE}?entry=gdtBanner`);
  };

  const closeHandler = (event) => {
    event.preventDefault();
    if (event.type === 'click' || event.key === 'Enter') {
      setShow(false);
    }
  };

  if (!show) return null;

  return (
    <GDTBannerStyled ref={gdtBannerRef} backgroundColor={backgroundColor || 'lightBlue'} button={button} sticky={sticky}>
      {!mobile && (
        <>
          <div className="gdt-banner__text-block">
            <span className="gdt-banner__text--title" data-testid="gdt-banner-text">
              {title || 'Help me choose'}
            </span>
            <span className="gdt-banner__text--subtitle" data-testid="gdt-banner-text">
              {subtitle || 'Try our 2-minute quiz to find the right plan for you.'}
            </span>
          </div>
          <div>
            <Button className="gdt-banner__button" onClick={eventHandler}>
              {button?.text || 'TAKE THE QUIZ'}
            </Button>
          </div>
        </>
      )}
      {mobile && (
        <button className="gdt-banner__mobile-button" onClick={eventHandler}>
          {title || `Can't decide? Try our 2-minute quiz`}
        </button>
      )}
      <CloseThin
        className="gdt-banner__close"
        role="button"
        tabIndex={0}
        onClick={closeHandler}
        onKeyDown={closeHandler}
      />
    </GDTBannerStyled>
  );
};

GDTBanner.displayName = 'GDTBanner';

export default GDTBanner;
